import type { ITheme } from '@xterm/xterm'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useHttpClient, useTheme, useTranslation } from '@molecule/app-react'

import { onPageResume, WS_SERVER_REAPED_AFTER_MS } from '../lib/pageResume.js'
import { ensureWsAuthToken } from '../lib/wsAuth.js'
import { buildWsUrl } from '../lib/wsUrl.js'
import { sandboxErrorCopy } from '../utilities/sandbox-error-copy.js'

interface TerminalMessage {
  type: 'output' | 'prompt' | 'error' | 'ready'
  data?: string
  /** On an `error` frame: the sandbox-state key (asleep / booting / lost) the IDE translates. */
  errorKey?: string
  cwd?: string
  /** 'pty' = a real shell (raw bytes both ways); 'exec' = one command per frame. */
  mode?: 'pty' | 'exec'
}

/**
 * The dark xterm palette — the panel's original hardcoded colors, kept
 * verbatim so dark theme renders exactly as before.
 */
const DARK_XTERM_THEME: ITheme = {
  background: '#0e0e0e',
  foreground: '#e0e0e0',
  cursor: '#4070e0',
  cursorAccent: '#0e0e0e',
  selectionBackground: 'rgba(64,112,224,0.3)',
  black: '#0e0e0e',
  red: '#c83c3c',
  green: '#309000',
  yellow: '#c8a000',
  blue: '#4070e0',
  magenta: '#9050c8',
  cyan: '#30a0a0',
  white: '#e0e0e0',
  brightBlack: '#555555',
  brightRed: '#ff6666',
  brightGreen: '#66cc33',
  brightYellow: '#ffcc00',
  brightBlue: '#6699ff',
  brightMagenta: '#cc66ff',
  brightCyan: '#66cccc',
  brightWhite: '#ffffff',
}

/**
 * The light-theme counterpart: same hue family per ANSI slot, darkened for
 * contrast on a white background (the dark palette's brights are illegible
 * there).
 */
const LIGHT_XTERM_THEME: ITheme = {
  background: '#ffffff',
  foreground: '#1a1a1a',
  cursor: '#4070e0',
  cursorAccent: '#ffffff',
  selectionBackground: 'rgba(64,112,224,0.2)',
  black: '#1a1a1a',
  red: '#b32d2d',
  green: '#217a00',
  yellow: '#8f6c00',
  blue: '#3055c8',
  magenta: '#7a3fb0',
  cyan: '#1f8080',
  white: '#c8c8c8',
  brightBlack: '#707070',
  brightRed: '#c83c3c',
  brightGreen: '#309000',
  brightYellow: '#9a7b00',
  brightBlue: '#4070e0',
  brightMagenta: '#9050c8',
  brightCyan: '#208a8a',
  brightWhite: '#1a1a1a',
}

/** Resolves the xterm palette for the app's current theme mode. */
const xtermThemeFor = (mode: 'light' | 'dark'): ITheme =>
  mode === 'light' ? LIGHT_XTERM_THEME : DARK_XTERM_THEME

/** Reconnect backoff: 1s, 2s, 4s, 8s, then every 15s. */
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 15_000

/** Close codes the client must NOT retry — retrying only repeats the rejection. */
const FATAL_CLOSE_CODES = new Set([4001, 4003, 4004, 4029])

interface TerminalPanelProps {
  projectId: string
  sandboxStatus: string
}

/** Minimal shape of the xterm instance this panel drives. */
interface XtermLike {
  write(data: string): void
  writeln(data: string): void
  clear(): void
  dispose(): void
  onData(cb: (data: string) => void): { dispose(): void }
  options: { theme?: ITheme }
  cols: number
  rows: number
}

export function TerminalPanel({ projectId, sandboxStatus }: TerminalPanelProps) {
  const { t } = useTranslation()
  const http = useHttpClient()
  // The app's theme drives the terminal palette (the old hardcoded dark-only
  // palette was unreadable chrome in light theme).
  const { mode } = useTheme()
  const termRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const termInstanceRef = useRef<XtermLike | null>(null)
  const fitAddonRef = useRef<{ fit(): void } | null>(null)
  const lineBufferRef = useRef('')
  const cwdRef = useRef('/workspace')
  /** 'pty' until the server says otherwise; set from the `ready` frame. */
  const sessionModeRef = useRef<'pty' | 'exec'>('pty')
  /** Whether the user has already been told this disconnect is being retried. */
  const noticeShownRef = useRef(false)
  const [connected, setConnected] = useState(false)
  const [initialized, setInitialized] = useState(false)

  // Read via a ref inside `connect` so a theme toggle never tears down the
  // WebSocket/terminal; the live instance restyles through the effect below.
  const modeRef = useRef(mode)
  useEffect(() => {
    modeRef.current = mode
    const term = termInstanceRef.current
    if (term) term.options.theme = xtermThemeFor(mode)
  }, [mode])

  // Same reasoning for the translator: `t` is only used for notices, and if its
  // identity is unstable the session effect re-runs — disposing the terminal,
  // dropping the shell and wiping the scrollback on an unrelated re-render.
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  // And for the HTTP client, which the session uses only to read the current
  // auth token when it (re)connects. Re-creating the terminal because a provider
  // handed back a new client object would lose the shell for no reason.
  const httpRef = useRef(http)
  useEffect(() => {
    httpRef.current = http
  }, [http])

  const writePrompt = useCallback(() => {
    const term = termInstanceRef.current
    if (!term) return
    const shortCwd =
      cwdRef.current === '/workspace' ? '~' : cwdRef.current.replace('/workspace/', '~/')
    term.write(`\r\n\x1b[36m${shortCwd}\x1b[0m \x1b[33m$\x1b[0m `)
  }, [])

  useEffect(() => {
    if (!projectId || sandboxStatus !== 'running') return

    let disposed = false
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    let resizeObserver: ResizeObserver | null = null
    let dataDisposable: { dispose(): void } | null = null

    const teardown = (): void => {
      disposed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      resizeObserver?.disconnect()
      dataDisposable?.dispose()
      const ws = wsRef.current
      wsRef.current = null
      if (ws) {
        ws.onclose = null
        ws.onerror = null
        ws.onmessage = null
        ws.close()
      }
      termInstanceRef.current?.dispose()
      termInstanceRef.current = null
      fitAddonRef.current = null
      setConnected(false)
      setInitialized(false)
    }

    const start = async (): Promise<void> => {
      // Dynamically import xterm
      const [{ Terminal }, { FitAddon }] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
      ])

      // Import CSS
      await import('@xterm/xterm/css/xterm.css')

      if (disposed || !termRef.current) return

      const term = new Terminal({
        cursorBlink: true,
        fontSize: 13,
        fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace",
        theme: xtermThemeFor(modeRef.current),
        allowTransparency: true,
        scrollback: 5000,
        // The shell echoes; a second local echo would double every character.
        convertEol: false,
      }) as unknown as XtermLike

      const fitAddon = new FitAddon()
      ;(term as unknown as { loadAddon(addon: unknown): void }).loadAddon(fitAddon)
      ;(term as unknown as { open(el: HTMLElement): void }).open(termRef.current)

      const fitAndReport = (): void => {
        try {
          fitAddon.fit()
        } catch (_error) {
          // best-effort: DOM may not be laid out yet; fit is advisory and safe to skip
          return
        }
        const ws = wsRef.current
        if (ws && ws.readyState === WebSocket.OPEN) {
          // The remote terminal has no way to observe the panel — it only knows
          // the size we tell it, which is what makes wrapped `npm`/`vite` output
          // line up with the visible width.
          ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }))
        }
      }

      requestAnimationFrame(fitAndReport)

      termInstanceRef.current = term
      fitAddonRef.current = fitAddon
      setInitialized(true)

      term.writeln('\x1b[2m--- Molecule Terminal ---\x1b[0m')

      dataDisposable = term.onData((data) => {
        const ws = wsRef.current
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // Never swallow input silently: a terminal that renders nothing as you
          // type reads as a frozen IDE. Say why, once per outage.
          if (!noticeShownRef.current) {
            noticeShownRef.current = true
            term.writeln(
              `\r\n\x1b[33m${tRef.current('workspace.terminal.reconnecting', undefined, {
                defaultValue: 'Disconnected — reconnecting…',
              })}\x1b[0m`,
            )
          }
          return
        }

        if (sessionModeRef.current === 'pty') {
          // Raw pass-through: the shell owns echo, line editing, history and
          // signals. Ctrl-C is byte 0x03 and reaches the foreground job.
          ws.send(JSON.stringify({ type: 'input', data }))
          return
        }

        // Fallback mode: no process to type at, so the panel does the line
        // editing itself and sends one whole command.
        const code = data.charCodeAt(0)
        if (data === '\r') {
          const command = lineBufferRef.current
          lineBufferRef.current = ''
          term.write('\r\n')
          ws.send(JSON.stringify({ type: 'command', data: command }))
        } else if (code === 127 || data === '\b') {
          if (lineBufferRef.current.length > 0) {
            lineBufferRef.current = lineBufferRef.current.slice(0, -1)
            term.write('\b \b')
          }
        } else if (code === 3) {
          lineBufferRef.current = ''
          term.write('^C')
          writePrompt()
        } else if (code === 12) {
          term.clear()
          writePrompt()
        } else if (code >= 32) {
          lineBufferRef.current += data
          term.write(data)
        }
      })

      resizeObserver = new ResizeObserver(fitAndReport)
      resizeObserver.observe(termRef.current)

      openSocket()
    }

    const scheduleReconnect = (): void => {
      if (disposed) return
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS)
      attempts += 1
      reconnectTimer = setTimeout(openSocket, delay)
    }

    // Every (re)connect goes through the WS ticket funnel: the ticket is a 6h
    // bearer, and a reconnect carrying an expired one is rejected at the
    // handshake (a FATAL close → no retry) — a tab left open overnight had a
    // permanently dead terminal until a manual refresh. No-op while usable.
    let connectPending = false
    function openSocket(): void {
      if (disposed || connectPending) return
      connectPending = true
      void ensureWsAuthToken(httpRef.current).then((token) => {
        connectPending = false
        if (disposed) return
        attachSocket(token)
      })
    }

    function attachSocket(token: string | null): void {
      const term = termInstanceRef.current
      const ws = new WebSocket(buildWsUrl(`/ws/projects/${projectId}/terminal`, token))
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        if (attempts > 0 && term) {
          term.writeln(
            `\r\n\x1b[2m${tRef.current('workspace.terminal.reconnected', undefined, {
              defaultValue: '--- Reconnected ---',
            })}\x1b[0m`,
          )
        }
        attempts = 0
        noticeShownRef.current = false
        // Tell the fresh session the size it is drawing into.
        if (term) ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }))
      }

      ws.onmessage = (event) => {
        let msg: TerminalMessage
        try {
          msg = JSON.parse(event.data) as TerminalMessage
        } catch (error) {
          console.warn('TerminalPanel: failed to parse WebSocket message', error)
          return
        }
        const live = termInstanceRef.current
        if (!live) return

        switch (msg.type) {
          case 'ready':
            sessionModeRef.current = msg.mode === 'exec' ? 'exec' : 'pty'
            if (msg.cwd) cwdRef.current = msg.cwd
            break
          case 'prompt':
            if (msg.cwd) cwdRef.current = msg.cwd
            if (sessionModeRef.current === 'exec') writePrompt()
            break
          case 'output':
            if (msg.data) {
              if (sessionModeRef.current === 'pty') {
                // Already terminal bytes — writing them verbatim is what keeps
                // colours, cursor moves and progress bars intact.
                live.write(msg.data)
              } else {
                const lines = msg.data.split('\n')
                for (let i = 0; i < lines.length; i++) {
                  if (i > 0) live.write('\r\n')
                  live.write(lines[i])
                }
              }
            }
            break
          case 'error': {
            // A sandbox-state refusal (asleep / booting / lost) carries an
            // errorKey — render the IDE's translated copy for it, the same
            // sentence every other panel shows; anything else is printed as sent.
            const copy = sandboxErrorCopy(msg.errorKey, t) ?? msg.data
            if (copy) {
              live.writeln(`\r\n\x1b[31m${copy}\x1b[0m`)
            }
            break
          }
        }
      }

      ws.onclose = (event) => {
        setConnected(false)
        if (disposed || wsRef.current !== ws) return
        wsRef.current = null
        const fatal = FATAL_CLOSE_CODES.has(event.code)
        if (term) {
          term.writeln(
            fatal
              ? `\r\n\x1b[31m${tRef.current('workspace.terminal.disconnectedFatal', undefined, {
                  defaultValue: '--- Disconnected ---',
                })}\x1b[0m`
              : `\r\n\x1b[2m${tRef.current('workspace.terminal.reconnecting', undefined, {
                  defaultValue: 'Disconnected — reconnecting…',
                })}\x1b[0m`,
          )
        }
        // A rejected connection (auth, role, missing project, too many
        // terminals) repeats forever if retried — only retry a session that
        // ENDED (idle timeout, shell exit, redeploy, network drop).
        if (!fatal) {
          noticeShownRef.current = true
          scheduleReconnect()
        }
      }

      ws.onerror = () => {
        // `onclose` always follows and owns the retry; nothing to do here but
        // reflect the state, so the dot never lies about being connected.
        setConnected(false)
      }
    }

    void start()

    // A page frozen long enough for the server's ping/pong to reap the socket
    // comes back with a zombie: `readyState` still says OPEN, keystrokes go
    // nowhere, and no `close` ever arrives to trigger the reconnect. The shell
    // behind it is already gone server-side (each connection is a fresh
    // session), so closing our copy loses nothing — onclose then runs the
    // normal reconnect. The gate is the reap window, so an ordinary tab switch
    // never drops a live shell.
    const offResume = onPageResume(({ hiddenForMs }) => {
      if (disposed || hiddenForMs < WS_SERVER_REAPED_AFTER_MS) return
      const ws = wsRef.current
      if (!ws) return
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        attempts = 0
        ws.close()
      }
    })

    return () => {
      offResume()
      teardown()
    }
  }, [projectId, sandboxStatus, writePrompt])

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        // Chrome matches the active xterm palette's background — the old
        // hardcoded dark background boxed a light theme in a black well.
        backgroundColor: xtermThemeFor(mode).background,
      }}
    >
      {/* Terminal header */}
      <div
        style={{
          height: 28,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 10px',
          borderBottom:
            mode === 'light' ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.06)',
          fontSize: 11,
          color: 'var(--mol-color-text-secondary, #888)',
        }}
      >
        <span>{t('workspace.terminal.title', undefined, { defaultValue: 'Terminal' })}</span>
        <span
          data-mol-id="terminal-status"
          title={
            connected
              ? t('workspace.terminal.connected', undefined, { defaultValue: 'Connected' })
              : t('workspace.terminal.reconnecting', undefined, {
                  defaultValue: 'Disconnected — reconnecting…',
                })
          }
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: connected ? '#309000' : '#808080',
          }}
        />
      </div>

      {/* Terminal content */}
      <div
        ref={termRef}
        style={{
          flex: 1,
          minHeight: 0,
          padding: initialized ? 0 : 8,
        }}
      >
        {sandboxStatus !== 'running' && !initialized && (
          <span style={{ color: '#888', fontSize: 12 }}>
            {t('workspace.terminal.waiting', undefined, {
              defaultValue: 'Waiting for sandbox...',
            })}
          </span>
        )}
      </div>
    </div>
  )
}
